import { CalendarSlot } from "@closerplatform/spinner-openapi";
import classNames from "classnames";
import { differenceInMilliseconds, isSameDay, isToday, setHours, startOfDay } from "date-fns";
import { observer } from "mobx-react";
import React from "react";
import { TooltipComponent } from "@fidget/common/components/tooltipComponent";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { WorkingInterval } from "@fidget/common/utils/getWorkingHours";
import { DAY_MILLIS, HOUR_MILLIS, TimeUtils } from "@fidget/common/utils/timeUtils";
import { ICalendarMeeting } from "../../models/calendar/CalendarMeeting";
import { isEdit, isReschedule, isScheduling } from "../../models/calendar/MeetingPopup";
import { Draggable } from "../draggable";
import { CalendarCell } from "./calendarCell";
import styles from "./calendarCell.css";
import { CalendarColumn } from "./calendarColumn";
import columnStyles from "./calendarColumn.css";
import { useStore } from "../../models";
import { CalendarCellMeetingTime } from "./calendarCellMeetingTime";
import { CalendarCellMeetingClient } from "./calendarCellMeetingClient";
import { staticConfig } from "@fidget/common/config/static-config";

interface Props {
  start: Date;
  weekly: boolean;
  workingHours: WorkingInterval;
}

export interface Entry {
  offsetFraction: number;
  fillFraction: number;
  element: JSX.Element;
}

export const CalendarDay = observer(({ start, weekly, workingHours }: Props) => {
  const { calendarStore } = useStore();

  const mkEntry = (element: JSX.Element, meetingStart: number, meetingDuration: number): Entry => {
    const timeSinceStart = differenceInMilliseconds(
      meetingStart,
      setHours(startOfDay(meetingStart), 0)
    );
    const timeSpan = DAY_MILLIS;
    return {
      element,
      offsetFraction: timeSinceStart / timeSpan,
      fillFraction: meetingDuration / timeSpan
    };
  };

  const mkGoogleMeeting = (meeting: CalendarSlot) => {
    const { importedCalendar } = calendarStore;
    const titleMaxLength = staticConfig.calendar.meetingTitleMaxLength;
    const tooltipText = meeting.title
      ? meeting.title.length > titleMaxLength
        ? meeting.title.slice(0, titleMaxLength) + "..."
        : meeting.title
      : intl.formatMessage("calendar.fromSyncedCal");
    const element = (
      <div key={meeting.start + tooltipText} className={styles["google-slot"]}>
        <TooltipComponent text={tooltipText} positionPreset={"top"}>
          <div className={styles["calendar-title"]}>{importedCalendar}</div>
        </TooltipComponent>
      </div>
    );
    return mkEntry(element, meeting.start, meeting.duration || 0);
  };

  const mkMeeting = (meeting: ICalendarMeeting): Entry => {
    const { popup } = calendarStore;

    const element = (
      <Draggable
        key={meeting.id}
        className={classNames(styles.meeting, {
          [styles["meeting-past"]]: meeting.start + meeting.duration < Date.now(),
          [styles["one-line"]]: meeting.duration <= HOUR_MILLIS / 4,
          [styles["meeting-moved"]]:
            popup &&
            (isReschedule(popup) || isEdit(popup)) &&
            popup.selected &&
            popup.selected === meeting.id
        })}
        onClick={() => calendarStore.createViewPopup(meeting)}
        onDragStart={(dragStart, offset) =>
          calendarStore.calendarEntryDragStarted(dragStart, offset, 200, meeting.roomId, meeting)
        }
        onDragEnd={() => calendarStore.calendarEntryDragEnded()}
      >
        <CalendarCellMeetingTime start={new Date(meeting.start)} duration={meeting.duration} />
        <CalendarCellMeetingClient guest={meeting.guest} />
      </Draggable>
    );
    return mkEntry(element, meeting.start, meeting.duration);
  };

  const mkSchedulingMeeting = (dateStart: Date): Entry | undefined => {
    const { popup } = calendarStore;
    if (popup && isScheduling(popup) && isSameDay(popup.start, dateStart)) {
      const element = (
        <div
          key={dateStart.valueOf()}
          className={classNames(styles.meeting, styles["calendar-snapped"])}
        >
          <CalendarCellMeetingTime start={popup.start} duration={popup.duration} />
          <CalendarCellMeetingClient guest={popup.profile} />
        </div>
      );
      return mkEntry(element, popup.start.getTime(), popup.duration);
    }
    return;
  };

  const isOutsideWorkingHours = (date: Date) => {
    const fromAsDate = new Date(workingHours.from);
    const toAsDate = new Date(workingHours.to);

    const isBeforeStart =
      fromAsDate.getHours() === date.getHours()
        ? fromAsDate.getMinutes() > date.getMinutes()
        : fromAsDate.getHours() > date.getHours();

    const isAfterEnd =
      toAsDate.getHours() === date.getHours()
        ? toAsDate.getMinutes() <= date.getMinutes()
        : toAsDate.getHours() <= date.getHours();

    return isBeforeStart || isAfterEnd;
  };

  const renderCells = () => {
    return TimeUtils.dailyCalendarRange(
      setHours(start, 0).valueOf(),
      setHours(start, 24).valueOf(),
      HOUR_MILLIS / 4
    )
      .map((h) => (
        <CalendarCell key={h.getTime()} time={h} outsideWorkingHours={isOutsideWorkingHours(h)} />
      ))
      .toArray();
  };

  const render = (): JSX.Element => {
    const isStartToday = isToday(start);
    const { meetings, googleMeetings } = calendarStore.filterByDay(start);
    const scheduling = mkSchedulingMeeting(start);
    const entries = meetings
      .map((m: ICalendarMeeting) => mkMeeting(m))
      .concat(scheduling ? [scheduling] : []);
    const googleEntries = googleMeetings.map((m: CalendarSlot) => mkGoogleMeeting(m));

    return (
      <CalendarColumn
        className={classNames({
          [columnStyles["calendar-column-highlighted"]]: weekly && isStartToday,
          [columnStyles["calendar-column-today"]]: isStartToday
        })}
        isToday={isStartToday}
        entries={entries}
        googleEntries={googleEntries}
        disablePointerEvents={!!calendarStore.draggingMeeting}
      >
        {renderCells()}
      </CalendarColumn>
    );
  };

  return render();
});
